import React, { useCallback, useState, useMemo, useEffect } from 'react';
import * as PR from "../../../prime-modules/index";
import { useSelector, useDispatch } from 'react-redux';
import { getLanguageTranslates, uploadTranslations, getCountriesList } from '../../../services/api';
import PopularCountryItem from './PopularCountryItem';
import "./PopularCountries.scss";

const PopularCountries = () => {
    const toast = React.useRef(null);
    const dispatch = useDispatch();
    const adminData = useSelector(state => state.adminAuth.adminSessionData);
    const headers = useMemo(() => {
        return { sessionid: adminData.sessionId };
    }, [adminData.sessionId]);

    const [popularCountries, setPopularCountries] = useState([]);
    const [allCountries, setAllCountries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editKey, setEditKey] = useState("");
    const [addDisable, setAddDisable] = useState(false);

    const fetchAllCountries = useCallback(() => {
        const handleCountriesResponse = (response) => {
            if (response.result === "SUCCESS") {
                const countriesData = response.data || [];
                const countriesList = countriesData.map(({ name, region, iso }) => ({
                    name: name.split('(')[0].trim(),
                    region,
                    iso
                }));
                setAllCountries(countriesList);
            }
        };
        getCountriesList(headers, dispatch, handleCountriesResponse);
    }, [dispatch, headers]);

    const fetchPopularCountries = useCallback(() => {
        setLoading(true);
        setEditKey("");
        setAddDisable(false);
        const getResponse = (response) => {
            setLoading(false);
            if (response.result === 'SUCCESS') {
                const assetData = response.data;
                try {
                    const popularCountriesRaw = assetData?.cmsData?.popularCountries?.selectedLanguage;
                    if (typeof popularCountriesRaw === 'string') {
                        setPopularCountries(popularCountriesRaw ? JSON.parse(popularCountriesRaw) : []);
                    } else if (Array.isArray(popularCountriesRaw)) {
                        setPopularCountries(popularCountriesRaw);
                    } else {
                        setPopularCountries([]);
                    }
                } catch (e) {
                    setPopularCountries([]);
                }
            } else if (response.result === "FAILED" && response.error) {
                const error = response.error;
                toast.current.show({ severity: error.severity, summary: 'Error', detail: error.errorMsg || error.summary });
            }
        };
        getLanguageTranslates('en', headers, dispatch, getResponse);
    }, [dispatch, headers]);

    useEffect(() => {
        fetchAllCountries();
        fetchPopularCountries();
    }, [fetchAllCountries, fetchPopularCountries]);

    const submitPopularCountriesHandler = async (updatedList) => {
        const request = {
            "cmsData": {
                "popularCountries": JSON.stringify(updatedList)
            }
        };
        setLoading(true);
        const getCMSResponse = (response) => {
            setLoading(false);
            setEditKey("");
            setAddDisable(false);
            if (response.result === 'SUCCESS') {
                toast.current.show({ severity: 'success', summary: 'Success', detail: 'Updated successfully' });
                setPopularCountries(updatedList);
                fetchPopularCountries();
            } else if (response.result === "FAILED" && response.error) {
                const error = response.error;
                toast.current.show({ severity: error.severity, summary: 'Error', detail: error.errorMsg || error.summary });
            }
        };
        uploadTranslations('en', request, headers, dispatch, getCMSResponse);
    };

    const addNewCountry = () => {
        const newItem = {
            name: "",
            region: "",
            iso: "",
            geog_type: "Local",
            id: `pc-${Date.now()}`
        };
        setPopularCountries(prev => [...prev, newItem]);
        setAddDisable(true);
        setEditKey(newItem.id);
    };

    const cancelNewItem = () => {
        setPopularCountries(prev => prev.filter(s => s.name && s.iso));
        setAddDisable(false);
        setEditKey("");
    };

    const saveHandler = (item, index, type = '') => {
        let updatedList = [...popularCountries];
        if (type === 'delete') {
            updatedList.splice(index, 1);
        } else {
            updatedList[index] = { ...item };
        }
        submitPopularCountriesHandler(updatedList);
    };

    const swapElements = (arr, i1, i2) => {
        if (i2 < 0 || i2 >= arr.length) return arr;
        const newArr = [...arr];
        [newArr[i1], newArr[i2]] = [newArr[i2], newArr[i1]];
        submitPopularCountriesHandler(newArr);
        return newArr;
    };

    const moveUp = (index) => setPopularCountries(prev => swapElements(prev, index, index - 1));
    const moveDown = (index) => setPopularCountries(prev => swapElements(prev, index, index + 1));

    return (
        <div className='general popular-countries-section'>
            <div className="card">
                <div className='popular-countries-list'>
                    <div className="paragraph-tag mb-4">
                        <h2>Add/Edit Popular Countries</h2>
                    </div>
                    <div className="mt-3 mb-4 items-container">
                        {popularCountries.map((item, idx) => (
                            <PopularCountryItem
                                key={item.id || idx}
                                item={item}
                                index={idx}
                                allCountries={allCountries}
                                saveHandler={saveHandler}
                                editKey={editKey}
                                setEditKey={setEditKey}
                                moveUp={moveUp}
                                moveDown={moveDown}
                                lastIndex={popularCountries.length - 1}
                                loading={loading}
                                cancelNewItem={cancelNewItem}
                            />
                        ))}
                    </div>
                    <PR.Button
                        label='+ Add New Popular Country'
                        className="add-faq-button"
                        onClick={addNewCountry}
                        disabled={addDisable || editKey || loading}
                    />
                </div>
                {loading && (
                    <div className="progress-spinner">
                        <PR.ProgressSpinner
                            className="progressSpinner"
                            animationDuration=".5s"
                        />
                    </div>
                )}
            </div>
            <PR.Toast ref={toast} position="top-right" />
        </div>
    );
};

export default PopularCountries;
